import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { ProductSortType } from "src/types/GlobalInterfaces";
import qs from "qs";
import {
  convertToApiSortKey,
  convertToKebabCase,
  getHeaderHeight,
  getLoginUserID,
} from "src/helpers/helperFunctions";
import ProductsViewContainer from "src/Components/Pharmacy/ProductsViewContainer";
import PriceRangeSelector from "src/Components/Pharmacy/PriceRangeSelector";
import SideTabList from "src/Components/Pharmacy/SideTabList";
import LabTestProductCard from "src/Components/WebsiteComponents/HomePage/Cards/LabTestProductCard";
import { ImageBaseUrl, QueryPath } from "src/lib/constans";
import { IPaginated } from "src/types/paginated";
import { useQueryParams } from "src/hooks/useQueryParams";
import { removeAllDisLike } from "src/Store/disLike";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";

const ProductByLabTestPage = () => {
  const [categoryLoader, setCategoryLoader] = useState<boolean>(false);
  const [categoryProducts, setCategoryProducts] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [allCategoriesLoader, setAllCategoriesLoader] =
    useState<boolean>(false);

  const [nextCursor, setNextCursor] = useState<{
    cursorId?: string;
    cursorPrice?: string;
    cursorKey?: string;
  }>({
    cursorId: "",
    cursorPrice: "",
    cursorKey: "",
  });

  const searchParams = useQueryParams();
  const categoryIds = searchParams.getParam(QueryPath.labTestCategory) || "";
  const search = searchParams.getParam(QueryPath.search) || "";
  const categoryName =
    searchParams.getParam(QueryPath.labTestCategoryName) || "";
  const navigate = useNavigate();
  const { testsDisLike } = useSelector((state: RootState) => state.disLike);
  const dispatch = useDispatch<AppDispatch>();
  const [queryParams, setQueryParams] = useState<{
    category: string[];
    page: number;
    limit: number;
    sort: ProductSortType;
    minPrice: number;
    maxPrice: number;
    search: string;
  }>({
    category: categoryIds ? categoryIds.split(",") : [],
    page: 1,
    limit: 12,
    sort: "most-popular",
    minPrice: 0,
    maxPrice: 100000,
    search: search || "",
  });

  const [bannerData, setBannerData] = useState<any>(null);
  const [bannerDataLoading, setBannerDataLoading] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });

  // Debouncing refs
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceDelay = 500; // 500ms delay for filters
  const searchDebounceDelay = 800; // 800ms delay for search
  const getBannerData = async (categoryId: string) => {
    setBannerDataLoading(true);
    const { res, err } = await httpRequest<any>({
      path: `/category/${categoryId}`,
    });
    if (res) {
      let d = res["0"];
      setBannerData({
        description: d?.categoryDescription,
        image: d?.categoryBanner,
        logo: d?.categoryImage,
        title: d?.name,
      });
    } else {
      toast(err, { type: "error" });
    }
    setBannerDataLoading(false);
  };

  const fetchCategoryProducts = async (
    params: Partial<typeof queryParams>,
    isLoadMore = false
  ) => {
    let q = {
      categoryIds: params.category?.join(",") || "",
      page: params.page,
      limit: params.limit,
      sortBy: convertToApiSortKey(params.sort || "most-popular"),
      userId: getLoginUserID(),
      minPrice: params.minPrice,
      maxPrice: params.maxPrice,
      search: params.search,
    };

    if (params.page === 1) {
      setCategoryProducts([]);
    }

    let newq = {
      ...q,
    };

    if (
      q.sortBy === "lowToHigh" ||
      (q.sortBy === "highToLow" && newq.page && newq.page > 1)
    ) {
      newq = {
        ...newq,
        ...nextCursor,
      };
    }

    if (params?.category?.length === 1 && params?.category[0] !== "") {
      getBannerData(params?.category[0]);
    } else {
      setBannerData({
        description: "",
        image: "",
        logo: "",
        title: "",
      });
    }

    setCategoryLoader(true);
    const { res, err } = await httpRequest<{
      documents: any[];
      paginated: IPaginated;
    }>({
      path: `/product/all-diagnostic-products?${qs.stringify(newq)}`,
    });
    if (res) {
      // Only append for load more, replace for new searches
      if (isLoadMore) {
        setCategoryProducts((prev) => {
          // Prevent duplicates by checking existing IDs
          const existingIds = new Set(prev.map((product) => product._id));
          const newData = res.documents.filter(
            (product) => !existingIds.has(product._id)
          );
          return [...prev, ...newData];
        });
      } else {
        setCategoryProducts(res.documents);
      }
      setPaginated(res.paginated);
      dispatch(removeAllDisLike("testsDisLike"));
      setNextCursor(res.paginated?.nextCursor || {});
    } else {
      toast(err, { type: "error" });
    }
    setCategoryLoader(false);
  };

  const changeQueryParams = (
    params: Partial<typeof queryParams>,
    update: boolean = true,
    reset: boolean = true,
    isSearch: boolean = false
  ) => {
    let qq = { ...queryParams, ...params };
    setQueryParams(qq);
    if (reset) {
      qq.page = 1;
    }
    if (update) {
      window.scrollTo(0, 0);
      setCategoryLoader(true);
      // Use different debouncing for search vs filters
      if (isSearch) {
        debouncedSearchProducts(qq);
      } else {
        debouncedFetchProducts(qq);
      }
    }
  };

  const getAllCategories = async () => {
    setAllCategoriesLoader(true);
    const { res, err } = await httpRequest<{ result: any[] }>({
      path: "/diagnostic-product/categories/all",
    });
    if (res) {
      setAllCategories(
        res.result.map((item) => ({
          label: item.name,
          value: item.id,
          id: item._id,
        }))
      );
    } else {
      toast(err, { type: "error" });
    }
    setAllCategoriesLoader(false);
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (searchDebounceTimeoutRef.current) {
        clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Debounced version of fetchCategoryProducts for filters
  const debouncedFetchProducts = useCallback((params: any) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for filters:", params);
      fetchCategoryProducts(params, false);
    }, debounceDelay);
  }, []);

  // Debounced version for search
  const debouncedSearchProducts = useCallback((params: any) => {
    // Clear existing timeout
    if (searchDebounceTimeoutRef.current) {
      clearTimeout(searchDebounceTimeoutRef.current);
    }

    // Set new timeout
    searchDebounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for search:", params);
      fetchCategoryProducts(params, false); // false = replace data, not append
    }, searchDebounceDelay);
  }, []);

  useEffect(() => {
    let category = categoryIds ? categoryIds.split(",") : [];
    const isSearchChange = search !== queryParams.search;
    changeQueryParams(
      {
        category: category || [],
        page: 1,
        search: search || "",
      },
      true,
      true,
      isSearchChange
    );
  }, [categoryIds, search]);

  return (
    <div className="w-full bg-banner-white-image">
      <div className="view-area-width flex gap-1 py-3 relative">
        <div
          className="p-[2px] product-side-bar-width custom-ai-scroll-bar !sticky overflow-y-auto"
          style={{
            height: `calc(100vh - ${getHeaderHeight()}px - 16px)`,
            top: `${getHeaderHeight() + 12}px`,
          }}
        >
          <div className="w-full box-design">
            <SideTabList
              title={`Lab Tests Categories ${
                allCategories.length > 0 ? `(${allCategories.length})` : 0
              }`}
              List={allCategories}
              onListClick={(item) => {
                let data = categoryIds.split(",") || [];
                let isExist = data.find((tm: any) => tm === item.id);
                if (isExist) {
                  data = data.filter((tm: any) => tm !== item.id);
                } else {
                  data = [...data, item.id];
                }

                let q = {};
                q = {
                  [QueryPath.labTestCategory]: allCategories
                    .filter((tm) => data.includes(tm.id))
                    ?.map((tm: any) => tm?.id)
                    .join(","),
                  [QueryPath.labTestCategoryName]: allCategories
                    .filter((tm) => data.includes(tm.id))
                    ?.map((tm: any) => tm?.label)
                    .join(","),
                };

                navigate(`/diagnostic-products?${qs.stringify(q)}`);
              }}
              showCheckBox={true}
              showRadio={false}
              loading={allCategoriesLoader}
              loadingText="Loading Lab Tests Categories"
              selectedItem={
                allCategories
                  .filter((item) => categoryIds.split(",").includes(item.id))
                  ?.map((tm: any) => tm.id) || []
              }
            />
            <PriceRangeSelector
              min={queryParams.minPrice}
              max={queryParams.maxPrice}
              onChange={(min, max) => {
                changeQueryParams({ minPrice: min, maxPrice: max });
              }}
              borderBottom={false}
            />
          </div>
        </div>
        <ProductsViewContainer
          breadCrumbData={[
            {
              label: "Home",
              onClick() {
                navigate("/");
              },
            },
            {
              label: "Lab Tests",
              onClick() {
                navigate("/lab-tests");
              },
            },
            ...(categoryName.length > 0 ? [{ label : categoryName.split(",").join(" , ") }] : []),
            ...(search ? [{ label: search }] : []),
          ]}
          heading={"Lab Tests"}
          loading={categoryLoader}
          renderSkeleton={() => (
            <LabTestProductCard skeleton={true} data={{}} />
          )}
          bannerData={bannerData}
          bannerLoading={bannerDataLoading}
          sortOptions={[
            {
              label: "Most Popular",
              value: "most-popular",
            },
            {
              label: "Most Rated",
              value: "most-rated",
            },
            {
              label: "Low to High",
              value: "price-low-to-high",
            },
            {
              label: "High to Low",
              value: "price-high-to-low",
            },
          ]}
          currentSort={queryParams.sort}
          onSortChange={(sort: string) => {
            changeQueryParams({ sort: sort as ProductSortType });
          }}
          products={categoryProducts}
          renderProduct={(item, index) => (
            <LabTestProductCard
              data={item}
              id={`add-test-to-cart-${index}`}
              index={index}
              onLikeProduct={(id, isLiked) => {
                setCategoryProducts((prev) =>
                  prev?.map((item) => {
                    if (item?._id === id) {
                      return { ...item, isLiked: isLiked };
                    }
                    return item;
                  })
                );
              }}
            />
          )}
          currentPage={queryParams.page}
          totalPages={paginated.totalPages}
          onLoadMore={(page) => {
            console.log("Page Changed to ", page);
            // For load more, we need to append data, not replace
            let updatedParams = { ...queryParams, page: page };
            setQueryParams(updatedParams);
            fetchCategoryProducts(updatedParams, true); // true = append data
          }}
          onLoadAgain={() => {
            changeQueryParams({});
          }}
        />
      </div>
    </div>
  );
};

export default ProductByLabTestPage;
